import React from 'react';
import { useTranslation } from 'react-i18next';
import { unescapeHTML, titleize } from 'underscore.string';
import { NavLink } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@cargomatic/ui';
import {
  Bell,
  CreditCard,
  FileText,
  LifeBuoy,
  LogOut,
  User,
} from 'lucide-react';
import CargomaticLogo from 'img/cargomatic-logo-dark.png';
import SessionStore from 'stores/SessionStore';
import AccountAccessRequestModal from 'components/modules/user/AccountAccessRequestModal';
import classNames from 'classnames';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import Navbar from '../v2/navs/Navbar';
import UserActionCreators from '../../actions/UserActionCreators';
import DollarIcon from '../v2/icons/DollarIcon';
import NavbarOverlayBanner from '../v2/navbar-overlay-banner';
import ShipmentSearch from './shipment-search';
import { Avatar } from '../modules/user/Avatar';
import LanguageSelector from './language-selector';
import { NavScrollFade } from './NavScrollFade';

const SUPPORT_MAILTO = 'mailto:support@cargomatic.com';

const navLinkClass =
  'group inline-flex h-10 items-center justify-center rounded-md px-3 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-700 hover:text-white focus:bg-gray-700 focus:text-white focus:outline-none disabled:pointer-events-none disabled:opacity-50';

const navLinkActiveClass = 'bg-gray-900 text-white';

const offersLinkClass =
  'inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-white bg-green-cm hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-green-500 mobile-carrier-nav-bar offers-offerlist';

function logout() {
  UserActionCreators.logout();
}

export default function CarrierNavigation() {
  const carrier = SessionStore.getUser();
  const hasAppointmentAccess =
    SessionStore.carrierHasAppointmentsAccess(carrier);

  const { t } = useTranslation();

  function renderMobileNav(onClose) {
    return (
      <>
        <NavLink
          className="nav-item"
          to="/shipments"
          onClick={onClose}
          key="mobile-shipments"
        >
          <span>{t('MY_SHIPMENTS')}</span>
        </NavLink>
        <AuthorizationWrapper
          excludedRegions={[REGIONS.AP]}
          key="payouts-auth-wrapper"
        >
          <NavLink
            className="nav-item"
            to="/carrier-payouts"
            onClick={onClose}
            key="mobile-carrier-payouts"
          >
            <span>{t('MY_PAYMENTS')}</span>
          </NavLink>
        </AuthorizationWrapper>
        <NavLink
          className="nav-item"
          to="/loadboard"
          onClick={onClose}
          key="mobile-loadboard"
        >
          <span>{titleize(t('OFFER', { count: 2 }))}</span>
        </NavLink>
        <NavLink
          className="nav-item mobile-carrier-menu offers-offerlist"
          to="/drivers"
          onClick={onClose}
          key="mobile-drivers"
        >
          <span>{t('MY_DRIVERS')}</span>
        </NavLink>
        {hasAppointmentAccess && (
          <NavLink className="nav-item" to="/appointments" key="appointments">
            <span>{t('MY_APPOINTMENTS')}</span>
          </NavLink>
        )}
        <NavLink
          className="nav-item"
          to="/carrier-center"
          onClick={onClose}
          key="mobile-profiles"
        >
          <span>{titleize(t('PROFILE', { count: 1 }))}</span>
        </NavLink>
        <NavLink
          className="nav-item"
          to={`/carriers/dashboard?carrier=${carrier._id}`}
          onClick={onClose}
          key="mobile-dashboard"
        >
          <span>{t('PENDING_TASKS')}</span>
        </NavLink>
        <NavLink
          className="nav-item"
          to="/documents"
          onClick={onClose}
          key="mobile-documents"
        >
          <span>{t('MY_DOCUMENTS')}</span>
        </NavLink>
        <NavLink
          className="nav-item"
          to="/billing"
          onClick={onClose}
          key="mobile-billing"
        >
          <span>{t('BILLING')}</span>
        </NavLink>
        <NavLink
          className="nav-item"
          to="/notifications"
          onClick={onClose}
          key="mobile-notifications"
        >
          <span>{titleize(t('NOTIFICATION', { count: 2 }))}</span>
        </NavLink>
        <a
          href={SUPPORT_MAILTO}
          className="nav-item"
          onClick={onClose}
          key="mobile-contact-support"
        >
          <span>{t('CONTACT_SUPPORT')}</span>
        </a>
        <div
          key="language-selector-mobile"
          className={classNames('nav-item', {
            hidden: LANGUAGE_SELECTION_DISABLED,
          })}
        >
          <LanguageSelector mobile />
        </div>
        <a href="#" className="nav-item" onClick={logout} key="mobile-signout">
          {t('SIGN_OUT')}
        </a>
      </>
    );
  }
  function renderRightNav() {
    const name = SessionStore.getUser().name;
    const userId = SessionStore.getUser()._id;

    const profileButton = (
      <button
        type="button"
        title={unescapeHTML(name)}
        className="flex-shrink-0 flex h-8 w-8 items-center justify-center text-sm bg-gray-800 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
        aria-haspopup="true"
        aria-label={unescapeHTML(name)}
      >
        <Avatar
          key={userId}
          userId={userId}
          altName={unescapeHTML(name)}
          className="w-8 h-8 rounded-full object-cover"
        />
      </button>
    );

    return [
      <div
        key="language-selector"
        className={classNames(
          'flex items-center justify-center nav-item mr-2',
          { hidden: LANGUAGE_SELECTION_DISABLED },
        )}
      >
        <LanguageSelector />
      </div>,
      <DropdownMenu key="main-dropdown">
        <DropdownMenuTrigger asChild>{profileButton}</DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem
            asChild
            className="gap-2 text-gray-900 no-underline hover:no-underline focus:bg-gray-100 focus:text-gray-900 focus:outline-none focus-visible:outline-none"
          >
            <NavLink to="/carrier-center">
              <User className="h-4 w-4" />
              <span>{titleize(t('PROFILE', { count: 1 }))}</span>
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem
            asChild
            className="gap-2 text-gray-900 no-underline hover:no-underline focus:bg-gray-100 focus:text-gray-900 focus:outline-none focus-visible:outline-none"
          >
            <NavLink to="/documents">
              <FileText className="h-4 w-4" />
              <span>{t('MY_DOCUMENTS')}</span>
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem
            asChild
            className="gap-2 text-gray-900 no-underline hover:no-underline focus:bg-gray-100 focus:text-gray-900 focus:outline-none focus-visible:outline-none"
          >
            <NavLink to="/billing">
              <CreditCard className="h-4 w-4" />
              <span>{t('BILLING')}</span>
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem
            asChild
            className="gap-2 text-gray-900 no-underline hover:no-underline focus:bg-gray-100 focus:text-gray-900 focus:outline-none focus-visible:outline-none"
          >
            <NavLink to="/notifications">
              <Bell className="h-4 w-4" />
              <span>{titleize(t('NOTIFICATION', { count: 2 }))}</span>
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            asChild
            className="gap-2 text-gray-900 no-underline hover:no-underline focus:bg-gray-100 focus:text-gray-900 focus:outline-none focus-visible:outline-none"
          >
            <a href={SUPPORT_MAILTO}>
              <LifeBuoy className="h-4 w-4" />
              <span>{t('CONTACT_SUPPORT')}</span>
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-2 text-gray-900 no-underline hover:no-underline focus:bg-gray-100 focus:text-gray-900 focus:outline-none focus-visible:outline-none"
            onClick={logout}
          >
            <LogOut className="h-4 w-4" />
            <span>{t('SIGN_OUT')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>,
    ];
  }
  const brandLogo = (
    <NavLink to={`/carriers/dashboard?carrier=${carrier._id}`} key="brand-logo">
      <img alt="Cargomatic" src={CargomaticLogo} style={{ height: 25 }} />
    </NavLink>
  );

  const overlay = (
    <NavbarOverlayBanner text={SessionStore.getUser().name || ''} showButton />
  );

  const isAuthorized = carrier.authorized;
  const isLoggedInAs = !!SessionStore.getLoginAs();

  return (
    <>
      <Navbar
        logo={brandLogo}
        renderRightNav={renderRightNav}
        renderMobileNav={renderMobileNav}
      >
        {isAuthorized && (
          <>
            <NavScrollFade key="primary-nav">
              <NavigationMenu className="max-w-none">
                <NavigationMenuList className="flex-nowrap items-center justify-start gap-1 space-x-0">
                  <NavigationMenuItem className="flex-shrink-0">
                    <NavigationMenuLink asChild>
                      <NavLink to="/loadboard" className={offersLinkClass}>
                        <DollarIcon />
                        <span className="ml-2">
                          {titleize(t('OFFER', { count: 2 }))}
                        </span>
                      </NavLink>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem className="flex-shrink-0">
                    <NavigationMenuLink asChild>
                      <NavLink
                        to={`/carriers/dashboard?carrier=${carrier._id}`}
                        className={navLinkClass}
                        activeClassName={navLinkActiveClass}
                      >
                        {t('CARRIER_DASHBOARD_PENDING_TASK')}
                      </NavLink>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem className="flex-shrink-0">
                    <NavigationMenuLink asChild>
                      <NavLink
                        to="/shipments"
                        className={navLinkClass}
                        activeClassName={navLinkActiveClass}
                      >
                        {t('MY_SHIPMENTS')}
                      </NavLink>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
                    <NavigationMenuItem className="flex-shrink-0">
                      <NavigationMenuLink asChild>
                        <NavLink
                          to="/carrier-payouts"
                          className={navLinkClass}
                          activeClassName={navLinkActiveClass}
                        >
                          {t('MY_PAYMENTS')}
                        </NavLink>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  </AuthorizationWrapper>
                  {hasAppointmentAccess && (
                    <NavigationMenuItem className="flex-shrink-0">
                      <NavigationMenuLink asChild>
                        <NavLink
                          to="/appointments"
                          className={navLinkClass}
                          activeClassName={navLinkActiveClass}
                        >
                          {t('MY_APPOINTMENTS')}
                        </NavLink>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  )}
                  <NavigationMenuItem className="flex-shrink-0">
                    <NavigationMenuLink asChild>
                      <NavLink
                        to="/drivers"
                        className={navLinkClass}
                        activeClassName={navLinkActiveClass}
                      >
                        {t('MY_DRIVERS')}
                      </NavLink>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem className="flex-shrink-0">
                    <NavigationMenuLink asChild>
                      <NavLink
                        to="/driver-tracking"
                        className={navLinkClass}
                        activeClassName={navLinkActiveClass}
                      >
                        {t('LIVE_VIEW')}
                      </NavLink>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </NavScrollFade>
            <div className="w-44 flex-shrink-0 xl:w-60" key="shipment-search">
              <ShipmentSearch
                menuStyle={{
                  position: 'fixed',
                  overflowX: 'hidden',
                  maxHeight: '50vh',
                  zIndex: 100,
                  cursor: 'pointer',
                  overflowY: 'scroll',
                }}
              />
            </div>
          </>
        )}
      </Navbar>
      <div key="overlay-item">{isLoggedInAs && overlay}</div>
      <AccountAccessRequestModal />
    </>
  );
}
